import { logger } from '@springtree/eva-sdk-core-logger';
import { Core } from '@springtree/eva-services-core';
import { atom, selector } from 'recoil';
import getEvaEndpoint from '../../helpers/get-eva-endpoint.js';
import userExpiration from '../../helpers/user-expiration.js';
import evaEndpointUrlState from '../core/eva-endpoint-url-state.js';
import currentUserTokenState from './current-user-token-state.js';
// Because the current user is used in the service state builder we cannot use
// the builder itself to make this service state
//
const recoilKey = 'currentUser';
// The stale state is used to manipulate the memoized function behavior of recoil
// A date/time string is used as the request ID.
// See: https://recoiljs.org/docs/guides/asynchronous-data-queries#use-a-request-id
//
const staleState = atom({
    key: `${recoilKey}/Stale`,
    default: `${new Date().valueOf()}`,
});
// Current user request has an empty payload but adding this for consistency
//
const requestState = atom({
    key: `${recoilKey}/Request`,
    default: undefined,
});
// Persisting the request state for this service serves no purpose
//
const storageState = selector({
    key: `${recoilKey}/Storage`,
    get: () => { return; },
});
// The response state can be undefined (initially), the service response
// or an error.
// Additional selectors are available to target either the Error or response
//
const responseRawState = selector({
    key: `${recoilKey}/Response/Raw`,
    get: async ({ get }) => {
        const evaEndpointUrl = get(evaEndpointUrlState);
        const currentUserToken = get(currentUserTokenState);
        get(staleState);
        if (!evaEndpointUrl ||
            !currentUserToken) {
            return;
        }
        const evaEndpoint = await getEvaEndpoint(evaEndpointUrl);
        try {
            const response = await evaEndpoint.callService(Core.GetCurrentUser, undefined, {
                authenticationToken: currentUserToken,
            });
            return response;
        }
        catch (error) {
            if (error.response?.status === 401) {
                logger.warn(`[${recoilKey}] User token has expired`, error)();
                userExpiration.tokenExpired(currentUserToken);
            }
            else {
                logger.warn(`[${recoilKey}] Call failed`, error)();
            }
            return error;
        }
    },
});
const responseResultState = selector({
    key: `${recoilKey}/Response/Result`,
    get: ({ get }) => {
        const responseRaw = get(responseRawState);
        if (responseRaw && !(responseRaw instanceof Error)) {
            return responseRaw;
        }
    },
});
const responseErrorState = selector({
    key: `${recoilKey}/Response/Error`,
    get: ({ get }) => {
        const responseRaw = get(responseRawState);
        if (responseRaw && responseRaw instanceof Error) {
            return responseRaw;
        }
    },
});
const currentUserState = {
    recoilKey,
    error: responseErrorState,
    request: requestState,
    response: responseResultState,
    responseRaw: responseRawState,
    stale: staleState,
    storage: storageState,
};
export default currentUserState;
//# sourceMappingURL=current-user-state.js.map